// Connections: FreeAgent / Xero (OAuth lives in cover-connect), the email
// forwarding address, a bank CSV upload, and Claude / ChatGPT connected to the
// account (GET/DELETE /me/app-connections). The engine's magic links can land
// here with ?provider=freeagent. cover-connect returns with ?pending=&ref= (the
// page confirms the connection as this customer) or ?error=.
import { useEffect, useRef, useState } from 'react';
import { ConnectBanner, useConnectReturn, type ConnectProvider } from '@cover/core/connect.tsx';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Bot, Copy, Landmark, Mail } from 'lucide-react';
import { ok } from '@cover/core/api/client.ts';
import type { AppConnection, Connection } from '@cover/core/api/types.ts';
import { PROVIDER_LABEL, ago, date, plural } from '@cover/core/format.ts';
import { cn } from '@cover/core/cn.ts';
import { Badge } from '@cover/core/ui/Badge.tsx';
import { Button } from '@cover/core/ui/Button.tsx';
import { Card, CardBody, CardHeader } from '@cover/core/ui/Card.tsx';
import { Dialog } from '@cover/core/ui/Dialog.tsx';
import { Load } from '@cover/core/ui/States.tsx';
import { useToast } from '@cover/core/ui/Toast.tsx';
import { api, keys, startOAuth, useAppConnections, useConnections, useProfile } from '../api.ts';
import { UploadButton } from '../components/Upload.tsx';
import { PageTitle } from '../Layout.tsx';
import { signOut, useBusinessId } from '../session.tsx';

const ACCOUNTING = [
  { provider: 'freeagent' as const, blurb: 'We read your sales, bills and fixed assets, so we can fill in your turnover and add tools you’ve bought.' },
  { provider: 'xero' as const, blurb: 'We read your sales, bills and fixed assets, so we can fill in your turnover and add tools you’ve bought.' },
];

function statsText(stats: Connection['stats']) {
  if (!stats) return null;
  const parts = [stats.facts ? plural(stats.facts, 'detail') : null, stats.assets ? plural(stats.assets, 'item') : null].filter(Boolean);
  return parts.length ? `Added ${parts.join(' and ')} so far` : null;
}

export function ConnectionsPage() {
  const connections = useConnections();
  const [params, setParams] = useSearchParams();
  const toast = useToast();
  const qc = useQueryClient();
  const b = useBusinessId();
  const highlight = params.get('provider');
  const { state, dismiss } = useConnectReturn({
    params,
    who: 'customer',
    clear: (keys) =>
      setParams(
        (p) => {
          keys.forEach((k) => p.delete(k));
          return p;
        },
        { replace: true },
      ),
    onConnected: () => qc.invalidateQueries({ queryKey: keys.connections(b) }),
  });
  const connectAgain = (provider: ConnectProvider) =>
    startOAuth(b, provider, `${window.location.origin}/connections`).then(
      (r) => window.location.assign(r.authorizeUrl),
      (e) => toast.fail(e),
    );

  return (
    <>
      <PageTitle lead="Connect the things you already use, so you don’t have to type it all in. Nothing we pull in is used until you’ve checked it.">Connections</PageTitle>
      <div className="mb-5" aria-live="polite">
        <ConnectBanner state={state} onDismiss={dismiss} onConnectAgain={connectAgain} onSignIn={() => signOut(window.location.pathname + window.location.search)} />
      </div>
      <Load q={connections}>
        {(list) => (
          <div className="space-y-5">
            <section aria-labelledby="acc" className="space-y-3">
              <h2 id="acc" className="font-semibold">
                Accounting software
              </h2>
              {ACCOUNTING.map((a) => (
                <AccountingCard key={a.provider} provider={a.provider} blurb={a.blurb} conn={list.find((c) => c.provider === a.provider)} highlight={highlight === a.provider} />
              ))}
            </section>
            <ForwardingCard conn={list.find((c) => c.provider === 'email_forward')} highlight={highlight === 'email_forward'} />
            <BankCsvCard highlight={highlight === 'bank_csv'} />
          </div>
        )}
      </Load>
      <ConnectedApps />
    </>
  );
}

function AccountingCard({ provider, blurb, conn, highlight }: { provider: 'freeagent' | 'xero'; blurb: string; conn?: Connection; highlight: boolean }) {
  const b = useBusinessId();
  const qc = useQueryClient();
  const toast = useToast();
  const [confirmOff, setConfirmOff] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (highlight) ref.current?.scrollIntoView({ block: 'center' });
  }, [highlight]);

  const connect = useMutation({
    mutationFn: () => startOAuth(b, provider, `${window.location.origin}/connections`),
    onSuccess: (r) => window.location.assign(r.authorizeUrl),
    onError: (e) => toast.fail(e),
  });
  const disconnect = useMutation({
    mutationFn: () => ok(api.DELETE('/connections/{connectionId}', { params: { path: { connectionId: conn!.id } } })),
    onSuccess: async () => {
      setConfirmOff(false);
      await qc.invalidateQueries({ queryKey: keys.connections(b) });
      toast.ok(`${PROVIDER_LABEL[provider]} disconnected.`);
    },
    onError: (e) => toast.fail(e),
  });
  const name = PROVIDER_LABEL[provider];
  const live = conn && conn.status !== 'revoked';

  return (
    <div ref={ref}>
      <Card className={cn(highlight && 'ring-2 ring-brand')}>
        <CardHeader
          as="h3"
          title={name}
          action={live ? <Badge tone={conn.status === 'error' ? 'bad' : conn.status === 'syncing' ? 'info' : 'ok'}>{conn.status === 'error' ? 'Needs attention' : conn.status === 'syncing' ? 'Syncing' : 'Connected'}</Badge> : undefined}
        />
        <CardBody className="space-y-3">
          {live ? (
            <>
              <p className="text-sm">
                {conn.externalAccountName && <strong>{conn.externalAccountName}. </strong>}
                {conn.lastSyncAt ? `Last checked ${ago(conn.lastSyncAt)}.` : 'Waiting for the first sync.'} {statsText(conn.stats)}
              </p>
              {conn.lastError && <p className="text-sm text-bad-fg">{conn.lastError}</p>}
              <div className="flex flex-wrap gap-2">
                {conn.status === 'error' && (
                  <Button size="sm" busy={connect.isPending} onClick={() => connect.mutate()}>
                    Reconnect
                  </Button>
                )}
                <Button size="sm" variant="secondary" onClick={() => setConfirmOff(true)}>
                  Disconnect
                </Button>
              </div>
            </>
          ) : (
            <>
              <p className="text-sm text-mid">{blurb}</p>
              <Button busy={connect.isPending} onClick={() => connect.mutate()}>
                Connect {name}
              </Button>
            </>
          )}
        </CardBody>
      </Card>
      <Dialog open={confirmOff} onOpenChange={setConfirmOff} title={`Disconnect ${name}?`} description="We’ll stop reading from it straight away. Anything we already added stays on your profile, marked as coming from it.">
        <div className="flex gap-2">
          <Button variant="danger" busy={disconnect.isPending} onClick={() => disconnect.mutate()}>
            Disconnect
          </Button>
          <Button variant="secondary" onClick={() => setConfirmOff(false)}>
            Keep it
          </Button>
        </div>
      </Dialog>
    </div>
  );
}

function ForwardingCard({ conn, highlight }: { conn?: Connection; highlight: boolean }) {
  const profile = useProfile();
  const b = useBusinessId();
  const qc = useQueryClient();
  const toast = useToast();
  const address = conn?.address ?? profile.data?.forwardingAddress ?? null;
  const create = useMutation({
    mutationFn: () => ok(api.POST('/businesses/{businessId}/connections', { params: { path: { businessId: b } }, body: { provider: 'email_forward' } })),
    onSuccess: () => Promise.all([qc.invalidateQueries({ queryKey: keys.connections(b) }), qc.invalidateQueries({ queryKey: keys.profile(b) })]),
    onError: (e) => toast.fail(e),
  });
  async function copy() {
    if (!address) return;
    try {
      await navigator.clipboard.writeText(address);
      toast.ok('Address copied.');
    } catch {
      toast.fail(new Error('Couldn’t copy. Press and hold the address to copy it.'));
    }
  }
  return (
    <Card className={cn(highlight && 'ring-2 ring-brand')}>
      <CardHeader title="Forward your receipts" action={<Mail className="h-5 w-5 text-mid" aria-hidden />} />
      <CardBody className="space-y-3">
        <p className="text-sm text-mid">Forward order emails from Screwfix, Toolstation and the like to your own address. We’ll add the tools to your equipment list for you to check. We never read your inbox.</p>
        {address ? (
          <div className="flex flex-wrap items-center gap-2">
            <code className="min-w-0 flex-1 select-all break-all rounded-md bg-canvas px-3 py-2.5 text-sm">{address}</code>
            <Button size="sm" variant="secondary" onClick={copy}>
              <Copy className="h-4 w-4" aria-hidden /> Copy
            </Button>
          </div>
        ) : (
          <Button busy={create.isPending} onClick={() => create.mutate()}>
            Get my address
          </Button>
        )}
        {conn?.lastSyncAt && <p className="text-sm text-mid">Last email {ago(conn.lastSyncAt)}. {statsText(conn.stats)}</p>}
      </CardBody>
    </Card>
  );
}

function BankCsvCard({ highlight }: { highlight: boolean }) {
  const qc = useQueryClient();
  const b = useBusinessId();
  const toast = useToast();
  return (
    <Card className={cn(highlight && 'ring-2 ring-brand')}>
      <CardHeader title="Upload a bank statement" action={<Landmark className="h-5 w-5 text-mid" aria-hidden />} />
      <CardBody className="space-y-3">
        <p className="text-sm text-mid">Download a CSV from your business bank’s app or website and upload it here. We look for your turnover and any tool purchases. Nothing changes until you or our team checks it.</p>
        <UploadButton
          label="Upload CSV"
          accept=".csv,text/csv"
          source="bank_csv"
          hint="bank_statement"
          onUploaded={async () => {
            await qc.invalidateQueries({ queryKey: keys.documents(b) });
            toast.ok('Statement uploaded. We’ll let you know if we find anything to check.');
          }}
        />
      </CardBody>
    </Card>
  );
}

function ConnectedApps() {
  const apps = useAppConnections();
  const qc = useQueryClient();
  const toast = useToast();
  const [confirm, setConfirm] = useState<AppConnection | null>(null);
  const disconnect = useMutation({
    mutationFn: (a: AppConnection) => ok(api.DELETE('/me/app-connections/{connectionId}', { params: { path: { connectionId: a.id } } })),
    onSuccess: async (_r, a) => {
      setConfirm(null);
      await qc.invalidateQueries({ queryKey: keys.apps });
      toast.ok(`${a.clientName} disconnected.`);
    },
    onError: (e) => toast.fail(e),
  });
  return (
    <section aria-labelledby="apps" className="mt-8 space-y-3">
      <h2 id="apps" className="font-semibold">
        Connected apps
      </h2>
      <Card>
        <CardBody>
          <Load q={apps}>
            {(list) =>
              list.length ? (
                <ul className="divide-y divide-line">
                  {list.map((a) => (
                    <li key={a.id} className="flex flex-wrap items-center gap-x-3 gap-y-2 py-3 first:pt-0 last:pb-0">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-mid ring-1 ring-inset ring-line" aria-hidden>
                        <Bot className="h-4 w-4" />
                      </span>
                      <p className="min-w-0 flex-1 text-sm">
                        <strong>{a.clientName}</strong>
                        <span className="text-mid">
                          {' '}
                          · connected {date(a.connectedAt)}
                          {a.lastUsedAt && ` · last used ${ago(a.lastUsedAt)}`}
                        </span>
                      </p>
                      <Button variant="secondary" onClick={() => setConfirm(a)} aria-label={`Disconnect ${a.clientName}`}>
                        Disconnect
                      </Button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-mid">
                  No apps connected. You can connect Pickle to Claude or ChatGPT: add <code className="rounded bg-canvas px-1.5 py-0.5">mcp.itspickle.app/mcp</code> as a custom connector.
                </p>
              )
            }
          </Load>
        </CardBody>
      </Card>
      <Dialog
        open={!!confirm}
        onOpenChange={(o) => !o && setConfirm(null)}
        title={`Disconnect ${confirm?.clientName ?? 'this app'}?`}
        description="It won’t be able to see your account or get quotes for you until you connect it again."
      >
        <div className="flex gap-2">
          <Button variant="danger" busy={disconnect.isPending} onClick={() => confirm && disconnect.mutate(confirm)}>
            Disconnect
          </Button>
          <Button variant="secondary" onClick={() => setConfirm(null)}>
            Keep it
          </Button>
        </div>
      </Dialog>
    </section>
  );
}
